import type { DigitalLectureDetail } from '~/models/digital-lecture'
import {
  COOKIE_DIG_LECTURE_LIKE_KEY,
  COOKIE_MAX_AGE_SECONDS,
  COOKIE_PATH,
} from '~/shared/const/cookie'

export function useCurrentDigitalLecture() {
  return useState<{ slug: string, id: number } | null>('digital-lecture-current', () => null)
}

export async function useDigitalLectureDetail(slug: string) {
  const { $services } = useNuxtApp()

  const likesCookie = useCookie<string[]>(COOKIE_DIG_LECTURE_LIKE_KEY, {
    path: COOKIE_PATH,
    maxAge: COOKIE_MAX_AGE_SECONDS,
    default: () => [],
  })

  // useState обязан вызываться до await, иначе теряется Nuxt-контекст
  const currentLecture = useCurrentDigitalLecture()

  onMounted(() => {
    void $services.digitalLectureService.detail.addPageView(slug)
  })

  const { data, error, refresh } = await useAsyncData<DigitalLectureDetail | null>(
    `digital-lecture-detail-${slug}`,
    () => $services.digitalLectureService.detail.getBySlug(slug),
  )

  const detail = computed(() => data.value ?? null)

  currentLecture.value = detail.value?.id ? { slug, id: detail.value.id } : null

  const isLiked = ref((likesCookie.value ?? []).includes(slug))
  const likesCount = ref(Number(data.value?.likes_count ?? 0))
  const isLikePending = ref(false)

  watch(detail, (value) => {
    if (isLikePending.value) return
    likesCount.value = Number(value?.likes_count ?? 0)
    isLiked.value = (likesCookie.value ?? []).includes(value?.slug ?? slug)
  })

  async function toggleLike() {
    if (!detail.value || isLiked.value || isLikePending.value) return

    const targetSlug = detail.value.slug
    isLikePending.value = true

    isLiked.value = true
    likesCount.value += 1

    try {
      const ok = await $services.digitalLectureService.detail.addLike(targetSlug)

      if (!ok) {
        isLiked.value = false
        likesCount.value = Math.max(0, likesCount.value - 1)
        return
      }

      const current = likesCookie.value ?? []
      if (!current.includes(targetSlug)) {
        likesCookie.value = [...current, targetSlug]
      }
    }
    finally {
      isLikePending.value = false
    }
  }

  return {
    detail,
    error,
    refresh,
    isLiked,
    likesCount,
    isLikePending,
    toggleLike,
  }
}
